import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { useProviderAccountMutations } from '@/api/hooks';
import type { ProviderAccount } from '@/api/schemas';
import { notAvailableYet } from '@/lib/mutationError';
import { hasErrors } from './credentialModel';
import { editErrors, editPatch, editSavedNote, shownError } from './providerAccountModel';
import { useSubmitState } from './useSubmitState';

/** What the edit sheet says about a failed save: not served yet, a stale etag (412), or the API's own problem. */
function editProblem(err: unknown): unknown {
  const unavailable = notAvailableYet(err);
  if (unavailable) return unavailable;
  return err instanceof ApiError && err.status === 412 ? new Error('Someone changed this account since you opened it. Close the sheet, reopen it and save again.') : err;
}

/** State and submit of the "Edit provider account" form: the name and the slots (the account name at the provider is fixed). */
export function useEditProviderForm(account: ProviderAccount, onDone: () => void) {
  const { patch } = useProviderAccountMutations();
  const [name, setName] = useState(account.name);
  const [concurrencyLimit, setConcurrencyLimit] = useState(String(account.concurrencyLimit));
  const sub = useSubmitState();
  const local = editErrors(name, concurrencyLimit);
  const errorFor = (field: keyof typeof local, live = false) => shownError({}, local, field, sub.submitted || live);

  const save = async (body: ReturnType<typeof editPatch>) => {
    try {
      const saved = await patch.mutateAsync({ id: account.id, body, etag: account.etag });
      toast.success(`${saved.name} saved`, { description: editSavedNote(body, account) });
      onDone();
    } catch (err) {
      sub.setProblem(editProblem(err));
    }
  };
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    sub.setSubmitted(true);
    sub.setProblem(null);
    if (hasErrors(local)) return;
    const body = editPatch(account, name, concurrencyLimit);
    if (Object.keys(body).length === 0) onDone();
    else await save(body);
  };
  return { name, setName, concurrencyLimit, setConcurrencyLimit, errorFor, onSubmit, problem: sub.problem, saving: patch.isPending };
}

export type EditProviderForm = ReturnType<typeof useEditProviderForm>;
